import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { Medication } from '../models/medication';
import { Settings } from './settings';
import { MedicationImage } from '../models/medicationimage';
import { PrecautionDirection } from '../models/precautiondirection';
import { Link } from '../models/link';
import { SupplierMedication } from '../models/supplier-medication';
import { MedicationFamily } from '../models/medicationfamily';
import { Ingredients } from '../models/ingredients';
import { MedSearchStructure } from '../models/med-search-structure';

@Injectable({
	providedIn: 'root'
})
export class MedicationService {

	constructor(private http: HttpClient) { }
	/** Medication Families */
	getAllMedicationFamily(): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/medication-family')
			.pipe(
				retry(1)
			);
	}
	getMedicationFamilyByItemPrefix(itemPref: string): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/medication-family/itemprefix/' + itemPref)
			.pipe(
				retry(1)
			);
	}
	getNextItemPrefixNumber(): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/next-itemprefix/')
			.pipe(
				retry(1)
			);


	}
	getAllMedicationFamilyFromSearch(ms: MedSearchStructure): Observable<any> {
		ms.setAll();
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/medication-family/search/' + ms.medDescription)
			.pipe(
				retry(1)
			);
	}
	createMedicationFamily(record: MedicationFamily): Observable<any> {
		return this.http.post(Settings.getInstance().BASE_URL + 'medications/medication-family', record)
			.pipe(
				retry(1)
			);
	}
	updateMedicationFamily(record: MedicationFamily): Observable<any> {
		return this.http.put(Settings.getInstance().BASE_URL + 'medications/medication-family/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	deleteMedicationFamily(id: number): Observable<any> {
		return this.http.delete(Settings.getInstance().BASE_URL + 'medications/medication-family/' + id)
			.pipe(
				retry(1)
			);
	}
	/** Medications **/
	getAllMedications(): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/medications')
			.pipe(
				retry(1)
			);
	}
	public getMedicationsFromSearch(ms: MedSearchStructure): Observable<any> {
		ms.setAll();
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/medications/search/' + ms.ndcNumber + '/' + ms.medDescription)
			.pipe(
				retry(1)
			);
	}
	public getMedicationsForOrderLookup(): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/medications-for-order-lookup')
			.pipe(
				retry(1)
			);
	}
	public getMedicationByNDC(ndc: string): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/medications/ndc/' + ndc)
			.pipe(
				retry(1)
			);
	}
	public getMedicationByRawNDC(rawNdc: string): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/search-raw-ndc/' + rawNdc)
			.pipe(
				retry(1)
			);
	}
	public getSupplierMedicationsBySupplierIdShort(id: number): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/supplier-medication-by-supplier-short/' + id)
			.pipe(
				retry(1)
			);
	}
	public createMedication(record: Medication): Observable<any> {
		return this.http.post(Settings.getInstance().BASE_URL + 'medications/medications', record)
			.pipe(
				retry(1)
			);
	}
	public updateMedication(record: Medication): Observable<any> {
		return this.http.put(Settings.getInstance().BASE_URL + 'medications/medications/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	public updateMedicationCostBase(id: number, costBase: number): Observable<any> {
		return this.http.put(Settings.getInstance().BASE_URL + 'medications/medications/costBase/' + id + '/' + costBase, null)
			.pipe(
				retry(1)
			);
	}
	public deleteMedication(id: number): Observable<any> {
		return this.http.delete(Settings.getInstance().BASE_URL + 'medications/medications/' + id)
			.pipe(
				retry(1)
			);
	}
	/** Medication Images **/
	public getAllMedicationImages(): Observable<MedicationImage[]> {
		return this.http.get<MedicationImage[]>(Settings.getInstance().BASE_URL + 'medications/medication-images')
			.pipe(
				retry(1)
			);
	}
	public getMedicationImagesByMedicationId(medicationId: number): Observable<MedicationImage[]> {
		return this.http.get<MedicationImage[]>(Settings.getInstance().BASE_URL + 'medications/medication-images-by-medication/' + medicationId)
			.pipe(
				retry(1)
			);
	}
	public getMedicationImagesByNDC(ndcNumber: string): Observable<MedicationImage[]> {
		return this.http.get<MedicationImage[]>(Settings.getInstance().BASE_URL + 'medications/medication-images/ndc/' + ndcNumber)
			.pipe(
				retry(1)
			);
	}
	public createMedicationImage(record: MedicationImage, image: any): Observable<any> {
		const formData: FormData = new FormData();
		formData.append('image', image, image.name);
		return this.http.post<any>(Settings.getInstance().BASE_URL + 'medications/medication-images/upload/image/' + record.medicationId + '/' + record.imageType, formData)
			.pipe(
				retry(0)
			);
	}
	public createMedicationImageFromBase64Encoded(record: MedicationImage, image: any, imageName: string): Observable<any> {
		const formData: FormData = this.base64ToFormData(image, imageName);
		//formData.append('image', image, image.name);
		return this.http.post<any>(Settings.getInstance().BASE_URL + 'medications/medication-images/upload/image/' + record.medicationId + '/' + record.imageType, formData)
			.pipe(
				retry(0)
			);
	}
	private base64ToFormData(base64: string, fileName = 'image.png'): FormData {
		const matches = base64.match(/^data:(.*);base64,(.*)$/);
		const mimeType = matches?.[1] || 'image/png';
		const base64Data = matches?.[2] || base64;

		const byteCharacters = atob(base64Data);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);

		const file = new File([byteArray], fileName, { type: mimeType });
		const formData = new FormData();
		formData.append('image', file);

		return formData;
	}
	public updateMedicationImage(record: MedicationImage): Observable<any> {
		return this.http.put(Settings.getInstance().BASE_URL + 'medications/medication-images/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	public deleteMedicationImage(id: number): Observable<any> {
		return this.http.delete(Settings.getInstance().BASE_URL + 'medications/medication-images/' + id)
			.pipe(
				retry(1)
			);
	}
	/** Directions and precautions **/
	public getMedicationDirectionsPrecautionsByMedicationId(medicationFamilyId: number): Observable<PrecautionDirection> {
		return this.http.get<PrecautionDirection>(Settings.getInstance().BASE_URL + 'medications/medication-directions-precautions-by-medication/' + medicationFamilyId)
			.pipe(
				retry(1)
			);
	}
	public createMedicationDirectionsPrecaution(record: PrecautionDirection): Observable<any> {
		return this.http.post(Settings.getInstance().BASE_URL + 'medications/medication-directions-precautions/', record)
			.pipe(
				retry(1)
			);
	}
	public updateMedicationDirectionsPrecaution(record: PrecautionDirection): Observable<any> {
		return this.http.put(Settings.getInstance().BASE_URL + 'medications/medication-directions-precautions/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	/** Medication Links **/
	public getMedicationLinksByMedicationId(medicationId: number): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/medication-links-by-medication/' + medicationId)
			.pipe(
				retry(1)
			);
	}
	public createMedicationLink(record: Link): Observable<any> {
		return this.http.post(Settings.getInstance().BASE_URL + 'medications/medication-links', record)
			.pipe(
				retry(1)
			);
	}
	public updateMedicationLink(record: Link): Observable<any> {
		return this.http.put(Settings.getInstance().BASE_URL + 'medications/medication-links/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	public deleteMedicationLink(id: number): Observable<any> {
		return this.http.delete(Settings.getInstance().BASE_URL + 'medications/medication-links/' + id)
			.pipe(
				retry(1)
			);
	}

	/** Manufacturers **/
	public getAllManufactureFor(): Observable<any> {
		return this.http.get(Settings.getInstance().BASE_URL + 'medications/manufacturefor/')
			.pipe(
				retry(1)
			);
	}
	public getAllManufactureBy(): Observable<any> {
		return this.http.get(Settings.getInstance().BASE_URL + 'medications/manufactureby/')
			.pipe(
				retry(1)
			);
	}

	public createManufactureFor(manufacturedFor: any): Observable<any> {
		return this.http.post(Settings.getInstance().BASE_URL + 'medications/manufacturefor/', manufacturedFor);
	}

	public createManufactureBy(manufacturedBy: any): Observable<any> {
		return this.http.post(Settings.getInstance().BASE_URL + 'medications/manufactureby/', manufacturedBy);
	}


	/** Medication Suppliers **/
	public getMedicationSuppliersByMedicationId(medicationId: number): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/supplier-medication-by-medication/' + medicationId)
			.pipe(
				retry(1)
			);
	}
	public getMedicationSuppliersByMedFamilyId(id: number): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/supplier-medication-by-medfamily/' + id)
			.pipe(
				retry(1)
			);
	}
	public createMedicationonSupplier(record: SupplierMedication): Observable<any> {
		return this.http.post(Settings.getInstance().BASE_URL + 'medications/supplier-medication', record)
			.pipe(
				retry(1)
			);
	}
	public updateMedicationonSupplier(record: SupplierMedication): Observable<any> {
		return this.http.put(Settings.getInstance().BASE_URL + 'medications/supplier-medication/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	public updateMedicationonSupplierByFK(record: SupplierMedication): Observable<any> {
		return this.http.put(Settings.getInstance().BASE_URL + 'medications/supplier-medication/FK/' + record.id, record)
			.pipe(
				retry(1)
			);
	}
	public deleteMedicationonSupplier(id: number): Observable<any> {
		return this.http.delete(Settings.getInstance().BASE_URL + 'medications/supplier-medication/' + id)
			.pipe(
				retry(1)
			);
	}

	/** Ingredients **/
	public getIngredientsByMedicationFamilyId(medicationId: number): Observable<any> {
		return this.http.get<any>(Settings.getInstance().BASE_URL + 'medications/ingredients-by-medication/' + medicationId)
			.pipe(
				retry(1)
			);
	}
	public createIngredients(record: Ingredients): Observable<any> {
		return this.http.post(Settings.getInstance().BASE_URL + 'medications/ingredients', record)
			.pipe(
				retry(1)
			);
	}
	public updateIngredients(record: Ingredients): Observable<any> {
		return this.http.put(Settings.getInstance().BASE_URL + 'medications/ingredients/', record)
			.pipe(
				retry(1)
			);
	}
	public deleteIngredients(id: number): Observable<any> {
		return this.http.delete(Settings.getInstance().BASE_URL + 'medications/ingredients/' + id)
			.pipe(
				retry(1)
			);
	}
}
